import { useContext, useEffect, useRef } from 'react';
import type { StripeCardNumberElement } from '@stripe/stripe-js';
import type braintree from 'braintree-web';
import { NewCardForm } from '../PaymentForms/NewCardForm';
import { CheckoutContext } from '@/context/CheckoutContext';
import { Disclaimer } from '../Common/Disclaimer';
import { IOFDisclaimer } from '../Common/IOFDisclaimer';
import { PayButton } from '../PaymentInputs/PayButton';
import { SaveCardCheckbox } from '../PaymentInputs/SaveCardCheckbox';
import { useTranslate } from '@/hooks/useTranslate';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { mixpanelService } from '@/services/mixpanelService';
import { Button } from '../Common/Button';
import { ButtonType } from '@/types/components';
import { Error } from '../Common/Error';
import { DrawerScreen } from '@/types/screens';
import { useIsPaymentMethodUnavailable } from '@/context/PaymentMethodAvailabilityContext';
import PaymentMethodNotSupported from '../PaymentMethod/PaymentMethodNotSupported';

export const AddNewCardForm = () => {
  const context = useContext(CheckoutContext);
  const {
    card: { setCardElementRef, initialCardPayload, setInitialCardPayload },
    drawer: { closeDrawer, screen },
    payment: { isFormValid },
    checkoutConfiguration,
    actions: { runPayment },
  } = context;
  const isUnavailable = useIsPaymentMethodUnavailable();
  const { t } = useTranslate();
  const cardElementRef = useRef<StripeCardNumberElement | braintree.HostedFields | null>(null);

  useEffect(() => {
    if (!cardElementRef.current) return;
    setCardElementRef(cardElementRef);
  }, [cardElementRef, setCardElementRef]);

  const handlePayment = () => {
    const properties = getMixpanelProperties(context);
    mixpanelService.track(MixpanelEvents.NEW_CARD_PAYMENT, properties as Record<string, unknown>);
    runPayment({
      newCard: true,
      isNewPaymentMethodSaved: initialCardPayload?.isSaveCard,
      isSavedPaymentMethodUsed: false,
    });
  };

  const handleCancel = () => {
    closeDrawer();
  };

  const handleSaveCardChange = (value: boolean) => {
    setInitialCardPayload({ ...initialCardPayload, isSaveCard: value });
  };

  // A card form that reported itself unavailable (see NewCardForm's provider
  // forms) makes the whole screen unpayable, so drop the header, save-card
  // checkbox and Pay button along with it rather than framing a dead form.
  if (isUnavailable) {
    return <PaymentMethodNotSupported />;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="color-card-foreground font-semibold text-base">{t('payWithDebitOrCreditCard')}</div>
      <Disclaimer />
      <div className="mt-2">
        <NewCardForm />
      </div>
      {checkoutConfiguration?.showSaveMyCard && (
        <div className="flex items-center w-full gap-2 mt-1">
          <SaveCardCheckbox isSaveCard={initialCardPayload?.isSaveCard ?? false} setIsSaveCard={handleSaveCardChange} />
        </div>
      )}
      <div className="flex flex-col gap-2 mt-5 w-full text-sm">
        <PayButton isDisabled={!isFormValid} onClick={handlePayment} />
      </div>
      <div className="flex flex-col mt-2 w-full">
        <Button type={ButtonType.LIGHT} onClick={() => handleCancel()}>
          {t('cancel')}
        </Button>
      </div>
      <IOFDisclaimer />
      {/* On mobile this form renders inside a full-screen drawer overlay
          (z-120) that hides the payment column's shared <Error /> (Payment.tsx
          suppresses it while a drawer screen is open), so a save-card failure
          must surface here. Scoped to NEW_CARD only — the other drawer screens
          should never show a payment error. Gating on the live `screen` (the
          drawer stays mounted ~350ms after it clears for the close animation,
          and closeDrawer doesn't reset the error) keeps this mutually exclusive
          with Payment's <Error />, so the two never render at once. */}
      {screen === DrawerScreen.NEW_CARD && <Error />}
    </div>
  );
};
